import type { LessonAiDraft } from "../auth/api-client";
import {
  deleteTeacherLessonTest,
  getTeacherLessonTests,
  patchTeacherLesson,
  postLibraryLesson,
  putTeacherLessonTourSteps,
  postTeacherLessonTest,
} from "../auth/api-client";
import { resolveLessonAiDraftPersistence } from "./course-template-helpers";

/** Persist an AI or manual draft to the teacher lesson library. Returns the lesson id. */
export async function saveLessonDraftToLibrary(
  bearer: string,
  draft: LessonAiDraft,
  opts?: {
    lessonId?: string | null;
    creationPrompt?: string | null;
    changelog?: string | null;
    isDraft?: boolean;
  }
): Promise<string> {
  const title = draft.title.trim();
  if (!title) throw new Error("Lesson title is required.");

  const { workspaceMode, starterFilesJson } = resolveLessonAiDraftPersistence(draft);

  const presentation = {
    creationPrompt: opts?.creationPrompt?.trim() || null,
    description: draft.description?.trim() || null,
    iconName: draft.iconName || null,
    iconBgColor: draft.iconBgColor || null,
    iconFgColor: draft.iconFgColor || null,
  };

  let lessonId = opts?.lessonId ?? null;
  if (lessonId) {
    await patchTeacherLesson(bearer, undefined, lessonId, {
      title,
      slug: draft.slug?.trim() || undefined,
      starterFilesJson,
      workspaceMode,
      ...(opts?.changelog ? { changelog: opts.changelog } : {}),
      ...(opts?.isDraft === true ? { isDraft: true } : opts?.isDraft === false ? { isDraft: false } : {}),
      ...presentation,
    });
    const existing = await getTeacherLessonTests(bearer, undefined, lessonId);
    for (const t of existing.tests) {
      await deleteTeacherLessonTest(bearer, undefined, lessonId, t.id);
    }
  } else {
    const { lesson } = await postLibraryLesson(bearer, {
      title,
      slug: draft.slug?.trim() || undefined,
      starterFilesJson,
      workspaceMode,
      ...(opts?.isDraft === true ? { isDraft: true } : {}),
      ...presentation,
    });
    lessonId = lesson.id;
  }

  await putTeacherLessonTourSteps(
    bearer,
    undefined,
    lessonId,
    draft.tourSteps.map((s) => ({
      title: s.title.trim(),
      body: s.body,
      referenceCode: s.referenceCode?.trim() ? s.referenceCode : null,
    }))
  );

  for (let i = 0; i < draft.tests.length; i++) {
    const t = draft.tests[i]!;
    await postTeacherLessonTest(bearer, undefined, lessonId, {
      name: t.name.trim(),
      testCode: t.testCode,
      isRequired: t.isRequired,
      visibility: t.visibility,
      description: t.description?.trim() || undefined,
    });
  }

  return lessonId;
}
